<script>
import { mapGetters } from 'vuex';
import { useBranding } from 'shared/composables/useBranding';
import SignupForm from './components/Signup/Form.vue';
import Testimonials from './components/Testimonials/Index.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: {
    SignupForm,
    Spinner,
    Testimonials,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  data() {
    return { isLoading: false };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    isAEasyDeskInstance() {
      return this.globalConfig.installationName === 'EasyDesk';
    },
  },
  beforeMount() {
    this.isLoading = this.isAEasyDeskInstance;
  },
  methods: {
    resizeContainers() {
      this.isLoading = false;
    },
  },
};
</script>

<template>
  <main
    class="relative min-h-screen overflow-hidden bg-gradient-to-r from-n-amber-3 via-white to-white dark:from-n-background dark:via-n-solid-2 dark:to-n-solid-3"
  >
    <div
      class="pointer-events-none absolute -top-40 left-1/2 h-80 w-[36rem] -translate-x-1/2 rounded-full bg-n-amber-5/50 blur-[160px] dark:bg-n-brand/10"
    />
    <div
      class="pointer-events-none absolute bottom-[-14rem] right-[-6rem] h-96 w-[34rem] rounded-full bg-n-amber-7/40 blur-[180px] dark:bg-n-blue-5/20"
    />

    <div class="relative min-h-screen">
      <div
        v-if="isLoading"
        class="absolute inset-0 z-40 flex items-center justify-center bg-white/75 backdrop-blur-lg dark:bg-n-background/70"
      >
        <Spinner color-scheme="primary" size="" />
      </div>

      <div
        class="relative flex min-h-screen flex-col-reverse transition-opacity duration-300 lg:flex-row"
        :class="isLoading ? 'opacity-60' : 'opacity-100'"
      >
        <Testimonials
          v-if="isAEasyDeskInstance"
          class="hidden flex-1 lg:flex"
          @resize-containers="resizeContainers"
        />

        <section
          class="relative flex flex-1 items-center justify-center px-6 py-16 sm:px-10 lg:px-20"
        >
          <div class="relative z-10 w-full max-w-lg">
            <div class="mb-12 space-y-8">
              <div class="flex items-center gap-3">
                <img
                  :src="globalConfig.logo"
                  :alt="globalConfig.installationName"
                  class="block h-9 w-auto shrink-0 dark:hidden"
                />
                <img
                  v-if="globalConfig.logoDark"
                  :src="globalConfig.logoDark"
                  :alt="globalConfig.installationName"
                  class="hidden h-9 w-auto shrink-0 dark:block"
                />
              </div>
              <span
                class="inline-flex items-center gap-2 rounded-full bg-n-amber-4 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-n-amber-11 shadow-sm ring-1 ring-n-amber-5/60"
              >
                <span class="i-lucide-rocket h-3.5 w-3.5" />
                {{ $t('REGISTER.INTRO_BADGE') }}
              </span>

              <div class="space-y-4">
                <h1 class="text-4xl font-bold leading-tight text-n-slate-12">
                  {{ $t('REGISTER.TRY_WOOT') }}
                </h1>
                <p class="text-base text-n-slate-11">
                  {{ replaceInstallationName($t('REGISTER.SUBTITLE')) }}
                </p>
              </div>
            </div>

            <div
              class="relative overflow-hidden rounded-3xl border border-n-amber-4 bg-white/95 p-8 shadow-[0_32px_96px_-48px_rgba(255,106,0,0.65)] backdrop-blur-xl ring-1 ring-n-amber-4/70 transition-all duration-300 dark:border-n-container/30 dark:bg-n-solid-2/90 dark:ring-n-container sm:p-10"
            >
              <div
                class="pointer-events-none absolute inset-x-10 -top-24 h-32 rounded-full bg-n-amber-5/50 blur-3xl dark:bg-n-brand/5"
              />
              <div class="relative space-y-8">
                <SignupForm />
                <div class="space-y-3 text-center text-sm text-n-slate-11">
                  <div>{{ $t('REGISTER.HAVE_AN_ACCOUNT') }}</div>
                  <router-link
                    class="inline-flex items-center justify-center gap-2 rounded-full bg-n-amber-9 px-4 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-n-amber-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-amber-8 focus-visible:ring-offset-2"
                    to="/app/login"
                  >
                    <span class="i-lucide-arrow-left h-4 w-4" />
                    {{ $t('REGISTER.CTA_LOGIN') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </main>
</template>
